import React, { useState } from 'react'
import { AxiosError } from 'axios'
import { useQuery } from 'react-query'
import { Trophy, TrendingUp, RefreshCw, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react'
import CircularProgress from '../../components/CircularProgress'
import StudentEvidenceTrendChart from '../../components/student/StudentEvidenceTrendChart'
import { api } from '../../lib/api'

interface LearningPlanProgress {
  fach: string
  learningPlansCount: number
  totalTasks: number
  completedTasks: number
  progress: number
  regularTasks: number
  completedRegularTasks: number
  baseProgress: number
  bonusTasks: number
  completedBonusTasks: number
  bonusProgress: number
  lpid?: number
  learningPlanName?: string
  startDate?: string | null
  endDate?: string | null
  isActive?: boolean
  buildingBlocks?: ActiveBuildingBlock[]
}

interface ActiveBuildingBlock {
  bid: number
  title: string
  fach: string
  learningPlans: LearningPlanProgress[]
}

interface DashboardResponse {
  student: {
    id: string
    vorname: string
    name: string
    klasse: string
  }
  progress: {
    overall: {
      totalTasks: number
      completedTasks: number
      progress: number
      baseProgress: number
      bonusProgress: number
    }
    subjects: {
      Deutsch: LearningPlanProgress | null
      Mathematik: LearningPlanProgress | null
      Englisch: LearningPlanProgress | null
    }
  }
  ranking: {
    place: number | null
    totalStudents: number
    studentProgress: number | null
    topProgress: number | null
    averageProgress: number | null
  }
  lookupDebug?: Record<string, unknown>
}

const fetchDashboard = async (): Promise<DashboardResponse> => {
  const response = await api.get('/student/dashboard')
  const { data, lookupDebug } = response.data || {}
  if (!data) {
    throw new Error('Unerwartete Server-Antwort: Feld "data" fehlt.')
  }

  return {
    ...data,
    lookupDebug: data.lookupDebug ?? lookupDebug
  }
}

function subjectProgressColor(fach: string) {
  if (fach === 'Mathematik') {
    return 'text-blue-500'
  }
  if (fach === 'Englisch') {
    return 'text-red-500'
  }
  return 'text-yellow-500'
}

const StudentDashboard: React.FC = () => {
  const [expandedArchivedBySubject, setExpandedArchivedBySubject] = useState<Record<string, boolean>>({})
  const isDebugMode = import.meta.env.DEV
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery<DashboardResponse, Error>('student-dashboard', fetchDashboard, {
    staleTime: 60 * 1000
  })

  const renderLoading = () => (
    <div className="flex items-center justify-center py-16">
      <div className="flex flex-col items-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        <p className="text-gray-500 text-sm">Dein Dashboard wird geladen...</p>
      </div>
    </div>
  )

  const renderError = () => {
    const axiosError = error as AxiosError<{ message?: string; debug?: Record<string, unknown> }>
    const errorMessage = axiosError?.response?.data?.message || error?.message || 'Das Dashboard konnte nicht geladen werden. Bitte versuche es erneut.'
    const errorDebug = axiosError?.response?.data?.debug

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-red-700">Fehler beim Laden</h2>
            <p className="text-red-600 text-sm mt-1">
              {errorMessage}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Erneut versuchen
            </button>
          </div>
        </div>

        {isDebugMode && errorDebug && (
          <div className="bg-red-900/80 text-red-100 rounded-lg border border-red-800 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-red-200">Debug-Daten</h3>
            <pre className="mt-2 text-xs whitespace-pre-wrap break-all">
              {JSON.stringify(errorDebug, null, 2)}
            </pre>
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return renderLoading()
  }

  if (isError || !data) {
    return renderError()
  }

  const { student, progress, ranking } = data
  const subjects = Object.values(progress.subjects).filter(Boolean) as LearningPlanProgress[]

  const formatPercent = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return 'keine Daten'
    }
    const rounded = Math.round(value * 10) / 10
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`
  }

  const studentProgressValue = ranking.studentProgress ?? progress.overall.progress ?? 0
  const topProgressValue =
    ranking.topProgress ?? (ranking.totalStudents > 0 ? studentProgressValue : null)
  const averageProgressValue =
    ranking.averageProgress ?? (ranking.totalStudents > 0 ? studentProgressValue : null)

  const isInTopTen = typeof ranking.place === 'number' && ranking.place > 0 && ranking.place <= 10
  const rankingSentence =
    isInTopTen && ranking.totalStudents
      ? `Damit liegst du auf Platz ${ranking.place} von ${ranking.totalStudents} Schülern in deiner Klasse.`
      : null

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schüler Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Hallo {student.vorname}! Hier siehst du deinen aktuellen Lernstand für die Klasse {student.klasse}.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Gesamtfortschritt</p>
              <p className="text-3xl font-semibold text-gray-900 mt-1">{progress.overall.progress}%</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-primary-600" />
            </div>
          </div>
          <div className="mt-4 space-y-2 text-sm text-gray-600">
            <p><span className="font-medium text-gray-800">Basis-Fortschritt:</span> {progress.overall.baseProgress}%</p>
            <p><span className="font-medium text-gray-800">Bonus-Fortschritt:</span> {progress.overall.bonusProgress}%</p>
            <p><span className="font-medium text-gray-800">Erledigte Aufgaben:</span> {progress.overall.completedTasks} / {progress.overall.totalTasks}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 col-span-1 lg:col-span-2">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-700 uppercase tracking-wide">Klassenwertung</p>
              {rankingSentence && (
                <p className="text-base text-gray-700 mt-1">{rankingSentence}</p>
              )}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Dein Fortschritt', value: formatPercent(studentProgressValue) },
              { label: 'Spitzenwert', value: formatPercent(topProgressValue) },
              { label: 'Klassenschnitt', value: formatPercent(averageProgressValue) }
            ].map((item) => (
              <div key={item.label} className="border border-amber-100 rounded-lg bg-amber-50/60 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-amber-700">{item.label}</p>
                <p className="text-xl font-semibold text-gray-900 mt-1">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Deine Fächer im Überblick</h2>
        {subjects.length > 0 ? (
          <div className="space-y-8">
            {subjects.map((subject) => {
              const color = subjectProgressColor(subject.fach)
              const blocks = subject.buildingBlocks
              const hasBlockLayout = Array.isArray(blocks) && blocks.length > 0

              if (hasBlockLayout) {
                const activeBlocks = blocks
                  .map((block) => ({
                    ...block,
                    learningPlans: block.learningPlans.filter((lp) => lp.isActive !== false)
                  }))
                  .filter((block) => block.learningPlans.length > 0)
                const archivedBlocks = blocks
                  .map((block) => ({
                    ...block,
                    learningPlans: block.learningPlans.filter((lp) => lp.isActive === false)
                  }))
                  .filter((block) => block.learningPlans.length > 0)
                const archivedCount = archivedBlocks.reduce((sum, block) => sum + block.learningPlans.length, 0)
                const isArchivedExpanded = Boolean(expandedArchivedBySubject[subject.fach])

                return (
                  <div key={subject.fach} className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">
                      {subject.fach}
                    </h3>
                    <div className="space-y-6">
                      {activeBlocks.map((block) => (
                        <div
                          key={`${subject.fach}-${block.bid}`}
                          className="bg-gray-50 rounded-lg border border-gray-100 p-4"
                        >
                          <h4 className="text-sm font-medium text-gray-900 mb-3">{block.title}</h4>
                          <div className="flex flex-wrap justify-center sm:justify-start gap-6">
                            {block.learningPlans.map((lp) => (
                              <CircularProgress
                                key={lp.lpid}
                                progress={lp}
                                color={color}
                                size={110}
                              />
                            ))}
                          </div>
                        </div>
                      ))}

                      {archivedCount > 0 && (
                        <div className="rounded-lg border border-gray-200 bg-white">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedArchivedBySubject((prev) => ({
                                ...prev,
                                [subject.fach]: !isArchivedExpanded
                              }))
                            }
                            className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            <span>Ältere Lernpläne anzeigen ({archivedCount})</span>
                            {isArchivedExpanded ? (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            )}
                          </button>

                          {isArchivedExpanded && (
                            <div className="px-4 pb-4 space-y-4">
                              {archivedBlocks.map((block) => (
                                <div
                                  key={`${subject.fach}-${block.bid}-archived`}
                                  className="bg-gray-50 rounded-lg border border-gray-100 p-4"
                                >
                                  <h4 className="text-sm font-medium text-gray-900 mb-3">{block.title}</h4>
                                  <div className="flex flex-wrap justify-center sm:justify-start gap-6">
                                    {block.learningPlans.map((lp) => (
                                      <CircularProgress
                                        key={lp.lpid}
                                        progress={lp}
                                        color={color}
                                        size={110}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              }

              return (
                <div key={subject.fach} className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">
                    {subject.fach}
                  </h3>
                  <div className="flex flex-col items-center sm:items-start p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <CircularProgress progress={subject} color={color} size={110} />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-600">Es liegen aktuell keine Lernfortschrittsdaten für deine Fächer vor.</p>
        )}
      </div>

      <StudentEvidenceTrendChart />
    </div>
  )
}

export default StudentDashboard

